import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import { ShipmentStatusTag } from '@/components';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import type { ShipmentConflict } from '@/services/embryo-service';

type EmbryoShipmentOverrideDialogProps = {
  visible: boolean;
  conflicts: ShipmentConflict[];
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const EmbryoShipmentOverrideDialog = ({
  visible,
  conflicts,
  isSubmitting = false,
  onConfirm,
  onCancel,
}: EmbryoShipmentOverrideDialogProps) => {
  const { t } = useTranslation();

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        label={t('common.cancel')}
        icon="pi pi-times"
        size="small"
        text
        disabled={isSubmitting}
        onClick={onCancel}
      />
      <Button
        label={t('shipping.overrideShipmentConfirm')}
        icon="pi pi-check"
        size="small"
        severity="warning"
        disabled={isSubmitting}
        loading={isSubmitting}
        onClick={onConfirm}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      header={t('shipping.overrideShipmentTitle')}
      footer={footer}
      style={{ width: '56rem' }}
      modal
      onHide={onCancel}
    >
      <p className="mb-4 text-sm text-gray-700">
        {t('shipping.overrideShipmentMessage', { total: conflicts.length })}
      </p>
      <div className="rounded-md border border-gray-200">
        <DataTable
          value={conflicts}
          dataKey="embryoId"
          size="small"
          className="p-datatable-sm"
          scrollable
          scrollHeight="20rem"
          emptyMessage={t('shipping.noConflictingEmbryos')}
        >
          <Column field="embryoName" header={t('common.name')} />
          <Column field="cane" header={t('semen.cane')} />
          <Column field="strawNumber" header={t('shipping.strawNumber')} />
          <Column
            field="shipmentNumber"
            header={t('shipping.previousShipment')}
          />
          <Column
            header={t('shipping.shipDate')}
            body={(rowData: ShipmentConflict) =>
              dateOnlyFormatter({ value: rowData.shipDate ?? '' })
            }
          />
          <Column field="shipFromSiteName" header={t('shipping.shipFromSite')} />
          <Column
            header={t('shipping.shipmentStatus')}
            body={(rowData: ShipmentConflict) => (
              <ShipmentStatusTag value={rowData.shipmentStatus} />
            )}
          />
        </DataTable>
      </div>
    </Dialog>
  );
};
